import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

import {
  Save,
  Loader2,
  CheckCircle,
  LayoutDashboard,
  Users,
  Sun,
  Moon,
  Upload,
} from "lucide-react";

export default function AdminPaymentSettings() {

  const navigate = useNavigate();

  /* FORM */
  const [paymentNumber, setPaymentNumber] = useState("");
  const [paymentName, setPaymentName] = useState("");
  const [personalNumber, setPersonalNumber] = useState("");

  /* CURRENT DATA */
  const [current, setCurrent] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  /* THEME */
  const [theme, setTheme] = useState(
    localStorage.getItem("admin-theme") || "light"
  );

  const isDark = theme === "dark";

  useEffect(() => {
    localStorage.setItem("admin-theme", theme);
  }, [theme]);

  const pageBg = isDark
    ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
    : "bg-gradient-to-br from-gray-50 via-white to-gray-100";

  const baseText = isDark ? "text-white" : "text-gray-900";
  const cardClass = isDark
    ? "bg-black/40 border border-white/10"
    : "bg-white border border-slate-900/15";
  const inputClass = isDark
    ? "w-full px-5 py-4 rounded-xl bg-black/40 border border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-sky-500 outline-none"
    : "w-full px-5 py-4 rounded-xl bg-white border border-blue-300 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500/30 outline-none";

  /* LOAD SETTINGS */
  useEffect(() => {
    async function loadSettings() {
      try {
        const snap = await getDoc(doc(db, "settings", "payment"));

        if (snap.exists()) {
          setCurrent(snap.data());
        }
      } catch (err) {
        console.error(err);
      }

      setLoading(false);
    }

    loadSettings();
  }, []);

  /* SAVE SETTINGS */

  const saveSettings = async () => {
    if (
      !paymentNumber.trim() ||
      !paymentName.trim() ||
      !personalNumber.trim()
    ) {
      alert("All payment fields are required");
      return;
    }

    setSaving(true);
    setSuccess(false);

    try {
      const payload = {
        paymentNumber: paymentNumber.trim(),
        paymentName: paymentName.trim(),
        personalNumber: personalNumber.trim(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "settings", "payment"), payload, {
        merge: true,
      });

      setCurrent(payload);

      setPaymentNumber("");
      setPaymentName("");
      setPersonalNumber("");

      setSuccess(true);

      alert(
        "Details saved for payment.\nKindly note payment payout dispatch is 9:00am every day."
      );

      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error(err);
      alert("Failed to save payment details");
    }

    setSaving(false);
  };

  /* LOADING */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <Loader2 className="animate-spin text-sky-400" size={36} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 pb-24 ${pageBg} ${baseText}`}>

      <div className="max-w-5xl mx-auto space-y-6">

        {/* HEADER */}

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
        >

          <div className="flex items-center gap-3">

            <div className="p-2 bg-gradient-to-r from-sky-500 to-blue-600 rounded-xl">
              <LayoutDashboard size={24} className="text-white" />
            </div>

            <div>
              <h1 className="text-xl font-bold text-sky-400">
                Payment Settings
              </h1>
              <p className="text-sm opacity-70">
                Manage payout details
              </p>
            </div>
          </div>

          {/* NAV */}

          <div className="flex flex-wrap gap-2">

            <NavBtn icon={<LayoutDashboard size={16} />} label="Dashboard" onClick={() => navigate("/admin/dashboard")} />
            <NavBtn icon={<Upload size={16} />} label="Upload WAEC" onClick={() => navigate("/admin/upload")} />
            <NavBtn icon={<Users size={16} />} label="Users" onClick={() => navigate("/admin/users")} />
            <NavBtn icon={<Upload size={16} />} label="Transactions" onClick={() => navigate("/admin/transactions")} />

            <button
              onClick={() => setTheme(isDark ? "light" : "dark")}
              className="p-2 bg-gray-800 rounded-lg"
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <NavBtn icon={<Save size={16} />} label="Save" onClick={saveSettings} />

          </div>
        </motion.div>

        {/* FORM */}

        <div className="max-w-xl mx-auto">

          <div className={`${cardClass} rounded-3xl p-8 space-y-6`}>

            <Input
              label="Payment Number"
              value={paymentNumber}
              setValue={setPaymentNumber}
              inputClass={inputClass}
            />

            <Input
              label="Payment Name"
              value={paymentName}
              setValue={setPaymentName}
              inputClass={inputClass}
            />

            <Input
              label="Personal Number"
              value={personalNumber}
              setValue={setPersonalNumber}
              inputClass={inputClass}
            />

            <button
              onClick={saveSettings}
              disabled={saving}
              className="w-full py-4 rounded-xl font-bold bg-green-600 hover:bg-green-500 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Update Settings
                </>
              )}
            </button>

            {success && (
              <div className="flex items-center justify-center gap-2 text-green-400">
                <CheckCircle size={18} />
                Updated successfully
              </div>
            )}
          </div>
        </div>

        {/* CURRENT */}

        {current && (
          <div className={`max-w-xl mx-auto ${cardClass} rounded-2xl p-6`}>

            <h2 className="text-lg font-bold text-sky-400 mb-4">
              Current Active Payment Details
            </h2>

            <div className="space-y-2 text-sm">

              <p>
                <span className="opacity-70">Payment Number:</span>{" "}
                <span className="font-bold">{current.paymentNumber}</span>
              </p>

              <p>
                <span className="opacity-70">Payment Name:</span>{" "}
                <span className="font-bold">{current.paymentName}</span>
              </p>

              <p>
                <span className="opacity-70">Personal Number:</span>{" "}
                <span className="font-bold">{current.personalNumber}</span>
              </p>

            </div>

          </div>
        )}
      </div>
    </div>
  );
}

/* SMALL COMPONENTS */

function NavBtn({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg flex items-center gap-2 text-sm"
    >
      {icon}
      {label}
    </button>
  );
}

function Input({ label, value, setValue, inputClass }) {
  return (
    <div>
      <label className="block mb-2 text-sm font-semibold opacity-80">
        {label}
      </label>

      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={`Enter ${label}`}
        className={inputClass}
      />
    </div>
  );
}